import { downloadCertificate, type CertificateData } from './certificateGenerator';

const API_BASE = (
  (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env?.VITE_SCRAPER_ENDPOINT)
  || 'https://api.checkcoa.com'
).replace(/\/+$/, '');

export const VERIFICATION_IMAGE_GENERATOR_VERSION = 'verification-image-v2';

export type VerificationSnapshotSource = 'result_card' | 'listing_builder';

export type VerificationSnapshot = {
  publicId: string;
  sourceType: VerificationSnapshotSource;
  generatorVersion: string;
  manifest: Record<string, unknown>;
  sha256: string | null;
  width: number | null;
  height: number | null;
  byteSize: number | null;
  preparedAt: string;
  issuedAt: string | null;
  supersededAt: string | null;
  binaryAvailable: boolean;
  contentUrl: string | null;
  verifyUrl: string;
  snapshotStatus: string;
  currentStatus: string;
  integrityMeaning: string;
};

export async function fetchVerificationSnapshot(publicId: string): Promise<VerificationSnapshot> {
  const response = await fetch(`${API_BASE}/verification-snapshots/${encodeURIComponent(publicId)}`);
  const payload = await response.json().catch(() => null) as { snapshot?: VerificationSnapshot; message?: string } | null;
  if (!response.ok || !payload?.snapshot) throw new Error(payload?.message || 'Verification original was not found.');
  return payload.snapshot;
}

type PrepareResponse = {
  snapshot: VerificationSnapshot;
  snapshotId?: string;
  uploadRequired: boolean;
  uploadToken?: string;
};

async function jsonRequest<T>(token: string, path: string, init: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body instanceof Blob ? { 'Content-Type': 'image/png' } : { 'Content-Type': 'application/json' }),
      ...init.headers,
    },
  });
  const payload = await response.json().catch(() => null) as { message?: string } | null;
  if (!response.ok) throw new Error(payload?.message || 'Verification image request failed.');
  return payload as T;
}

export async function getOrCreateVerificationPng(options: {
  token: string;
  sourceType: VerificationSnapshotSource;
  sourceKey: string;
  entitlementVariant: string;
  manifest: Record<string, unknown>;
  buildData: (registration: { publicId: string; verifyUrl: string; issuedAt: string }) => CertificateData;
  render: (data: CertificateData) => Promise<Blob>;
}): Promise<{ blob: Blob; snapshot: VerificationSnapshot }> {
  if (!options.token) throw new Error('Sign in to create or download a verification PNG.');
  const prepared = await jsonRequest<PrepareResponse>(options.token, '/verification-snapshots', {
    method: 'POST',
    body: JSON.stringify({
      sourceType: options.sourceType,
      sourceKey: options.sourceKey,
      entitlementVariant: options.entitlementVariant,
      generatorVersion: VERIFICATION_IMAGE_GENERATOR_VERSION,
      manifest: options.manifest,
    }),
  });

  if (!prepared.uploadRequired && prepared.snapshot.contentUrl) {
    const response = await fetch(prepared.snapshot.contentUrl);
    if (!response.ok) throw new Error('The registered verification PNG is temporarily unavailable.');
    return { blob: await response.blob(), snapshot: prepared.snapshot };
  }
  if (!prepared.snapshotId || !prepared.uploadToken) throw new Error('Verification image upload was not initialized.');

  const issuedAt = prepared.snapshot.issuedAt || prepared.snapshot.preparedAt || new Date().toISOString();
  const blob = await options.render(options.buildData({
    publicId: prepared.snapshot.publicId,
    verifyUrl: prepared.snapshot.verifyUrl,
    issuedAt,
  }));
  const uploaded = await jsonRequest<{ snapshot: VerificationSnapshot }>(
    options.token,
    `/verification-snapshots/${prepared.snapshotId}/content?token=${encodeURIComponent(prepared.uploadToken)}`,
    { method: 'PUT', body: blob },
  );
  return { blob, snapshot: uploaded.snapshot };
}

export async function shareOrDownloadPng(blob: Blob, fileName: string, title: string): Promise<'shared' | 'downloaded'> {
  const file = new File([blob], fileName, { type: 'image/png' });
  if (typeof navigator.share === 'function' && typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title });
      return 'shared';
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return 'shared';
      // Browsers may advertise file sharing but reject a concrete target. Download
      // is the deterministic fallback and keeps the issued original unchanged.
    }
  }
  downloadCertificate(blob, fileName);
  return 'downloaded';
}

export async function sha256File(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
